import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, OnDestroy } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import dayjs, { Dayjs } from 'dayjs';
import { buffer, debounceTime, Subject, Subscription, take } from 'rxjs';
import { MonitoringService } from './monitoring.service';

interface IErrorHandler {
  message: string;
  stack: string;
  timestamp: Dayjs;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandlerService implements ErrorHandler, OnDestroy {
  private errorList: IErrorHandler[] = [];
  private static chunkErrorSubject = new Subject<{ err: Error; from: 'global' | 'router' }>();

  private chunkErrorSubscription: Subscription = GlobalErrorHandlerService.chunkErrorSubject
    .pipe(buffer(GlobalErrorHandlerService.chunkErrorSubject.pipe(debounceTime(200))))
    .subscribe((chunkErrors) => {
      const snackBar: MatSnackBar = this.injector.get(MatSnackBar);
      const translate: TranslateService = this.injector.get(TranslateService);

      const routerSpecificError: any = chunkErrors.find((error) => error?.from === 'router');
      const actionLabel: string = translate.instant('button.refresh');

      if (routerSpecificError) {
        const routerErrorRef: MatSnackBarRef<TextOnlySnackBar> = snackBar.open(
          translate.instant('system.message.routerErrorMessage') as string,
          actionLabel,
          {
            duration: 5000,
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
          },
        );

        routerErrorRef
          .afterDismissed()
          .pipe(take(1))
          .subscribe(() => window.location.reload());

        return;
      }

      const backgroundErrorRef: MatSnackBarRef<TextOnlySnackBar> = snackBar.open(
        translate.instant('system.message.backgroundErrorMessage') as string,
        actionLabel,
        {
          horizontalPosition: 'right',
          verticalPosition: 'bottom',
        },
      );

      backgroundErrorRef
        .onAction()
        .pipe(take(1))
        .subscribe(() => {
          window.location.reload();
        });
    });

  constructor(
    private injector: Injector,
    private monitoringService: MonitoringService,
  ) {}

  public handleError(error: Error | HttpErrorResponse): void {
    let isExistError = false;

    if (error.hasOwnProperty('message') && error.hasOwnProperty('stack') && error instanceof Error) {
      isExistError = this.isErrorExistWithinInterval(error);
    }

    if (isExistError) {
      return;
    }

    this.monitoringService.logException(error);
  }

  private isErrorExistWithinInterval(error: Error): boolean {
    const errorInterval = 30;
    const { stack, message } = error;
    const index: number = this.errorList.findIndex(
      (item: IErrorHandler) => item.message === message && item.stack === stack,
    );
    const isExist: IErrorHandler | undefined = this.errorList[index];
    const now: Dayjs = dayjs.tz();

    if (!isExist) {
      this.errorList.push({
        message: error.message,
        stack: stack!,
        timestamp: now,
      });

      return false;
    }

    const diff: number = now.diff(isExist.timestamp, 'seconds');
    isExist.timestamp = now;

    return errorInterval >= diff;
  }

  public static handleChunkError(payload: { err: Error; from: 'global' | 'router' }): boolean {
    if (payload?.err && payload.err instanceof TypeError) {
      const msg = payload.err.message.toLowerCase();

      const isChunkError =
        msg.includes('failed to fetch dynamically imported module') ||
        msg.includes('error loading dynamically imported module') ||
        msg.includes('importing a module script failed');

      if (isChunkError) {
        GlobalErrorHandlerService.chunkErrorSubject.next(payload);

        return true;
      }
    }

    return false;
  }

  public ngOnDestroy(): void {
    this.chunkErrorSubscription?.unsubscribe();

    if (!GlobalErrorHandlerService.chunkErrorSubject.closed) {
      GlobalErrorHandlerService.chunkErrorSubject.complete();
    }
  }
}
